import { MAX_ENCRYPTED_UPLOAD_BYTES } from "./imageAttachment";

export const ATTACHMENT_CACHE_STORAGE_KEY = "styx-attachment-cache-settings-v1";

/** Не кэшировать вложения больше этого размера (по умолчанию 15 МБ). */
export const DEFAULT_MAX_ENTRY_MB = 15;
/** LRU-потолок всего кэша (по умолчанию 200 МБ). */
export const DEFAULT_MAX_TOTAL_MB = 200;

export const MIN_ENTRY_MB = 1;
export const MIN_TOTAL_MB = 50;
export const MAX_TOTAL_MB = 1000;

/** Верхняя граница «на файл» — не больше лимита загрузки. */
export function maxEntryMbCap(): number {
  return Math.max(1, Math.ceil(MAX_ENCRYPTED_UPLOAD_BYTES / (1024 * 1024)));
}

export type AttachmentCacheSettings = {
  maxEntryMb: number;
  maxTotalMb: number;
};

function clampInt(n: number, lo: number, hi: number, fallback: number): number {
  if (!Number.isFinite(n)) return fallback;
  return Math.min(hi, Math.max(lo, Math.round(n)));
}

export function normalizeAttachmentCacheSettings(raw: Partial<AttachmentCacheSettings> | null): AttachmentCacheSettings {
  const entryCap = maxEntryMbCap();
  const entry = clampInt(Number(raw?.maxEntryMb), MIN_ENTRY_MB, entryCap, DEFAULT_MAX_ENTRY_MB);
  let total = clampInt(Number(raw?.maxTotalMb), MIN_TOTAL_MB, MAX_TOTAL_MB, DEFAULT_MAX_TOTAL_MB);
  if (total < entry) {
    total = Math.min(MAX_TOTAL_MB, entry);
  }
  return { maxEntryMb: entry, maxTotalMb: total };
}

export function loadAttachmentCacheSettings(): AttachmentCacheSettings {
  if (typeof localStorage === "undefined") {
    return normalizeAttachmentCacheSettings(null);
  }
  try {
    const parsed = JSON.parse(localStorage.getItem(ATTACHMENT_CACHE_STORAGE_KEY) ?? "{}") as Partial<AttachmentCacheSettings>;
    return normalizeAttachmentCacheSettings(parsed);
  } catch {
    return normalizeAttachmentCacheSettings(null);
  }
}

export function saveAttachmentCacheSettings(next: Partial<AttachmentCacheSettings>): AttachmentCacheSettings {
  const prev = loadAttachmentCacheSettings();
  const normalized = normalizeAttachmentCacheSettings({ ...prev, ...next });
  localStorage.setItem(ATTACHMENT_CACHE_STORAGE_KEY, JSON.stringify(normalized));
  window.dispatchEvent(new CustomEvent("styx-attachment-cache-settings"));
  return normalized;
}

export function getAttachmentCacheMaxEntryBytes(): number {
  return loadAttachmentCacheSettings().maxEntryMb * 1024 * 1024;
}

export function getAttachmentCacheMaxTotalBytes(): number {
  return loadAttachmentCacheSettings().maxTotalMb * 1024 * 1024;
}
